// Data layer for billing. Reads go through Supabase directly (RLS).
// Stripe-related writes go through FastAPI (secret keys are server-side).

import { supabase, isSupabaseConfigured } from "./supabase";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// ---------------------------------------------------------------------------
// Shape helpers
// ---------------------------------------------------------------------------

export function centsToUsd(cents, currency = "usd") {
  if (typeof cents !== "number") return "—";
  const dollars = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
      minimumFractionDigits: 2,
    }).format(dollars);
  } catch {
    return `$${dollars.toFixed(2)}`;
  }
}

export function invoiceStatusLabel(status) {
  switch (status) {
    case "draft":  return "Draft";
    case "issued": return "Due";
    case "paid":   return "Paid";
    case "failed": return "Failed";
    case "void":   return "Voided";
    default:       return status || "—";
  }
}

/** Calm, parent-safe description of the autopay retry state for an
 *  invoice. Returns null when there's nothing worth saying (no failure
 *  yet, or already paid). The admin side uses the same helper for
 *  consistency — one source of truth for retry copy. */
export const AUTOPAY_MAX_ATTEMPTS = 3;

export function describeAutopayRetry(invoice) {
  if (!invoice) return null;
  if (invoice.status !== "issued") return null;
  const attempts = invoice.autopay_attempt_count || 0;
  if (attempts === 0) return null;
  const maxed = attempts >= AUTOPAY_MAX_ATTEMPTS;
  const nextAt = invoice.autopay_next_retry_at
    ? new Date(invoice.autopay_next_retry_at)
    : null;
  if (maxed) {
    return {
      kind: "needs_action",
      attempts,
      label: "Max attempts reached",
      detail: "Please pay manually or re-enroll autopay with a new card.",
    };
  }
  if (nextAt && nextAt.getTime() > Date.now()) {
    return {
      kind: "scheduled",
      attempts,
      nextAt,
      label: `Next retry ${formatRetryWhen(nextAt)}`,
      detail: "You can also pay manually now — no need to wait.",
    };
  }
  return {
    kind: "pending",
    attempts,
    label: "Retry queued",
    detail: "We'll try again on the next scheduled run.",
  };
}

function formatRetryWhen(d) {
  const now = Date.now();
  const diffMs = d.getTime() - now;
  const hours = Math.round(diffMs / 3_600_000);
  if (hours <= 0) return "shortly";
  if (hours < 24) return `in ~${hours}h`;
  const days = Math.round(hours / 24);
  if (days === 1) return "tomorrow";
  if (days < 7) return `in ${days} days`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export const LINE_CATEGORIES = [
  "tuition", "registration", "supply", "late_fee", "discount", "other",
];
export const LINE_CATEGORY_LABELS = {
  tuition: "Tuition",
  registration: "Registration",
  supply: "Supplies",
  late_fee: "Late fee",
  discount: "Discount",
  other: "Other",
};

// ---------------------------------------------------------------------------
// Admin — family list with billing profile + open balance
// ---------------------------------------------------------------------------

export async function listBillingFamilies() {
  if (!isSupabaseConfigured) return [];
  const { data: profiles, error } = await supabase
    .from("billing_profiles")
    .select(`
      id, family_id, billing_email, stripe_customer_id, status, autopay,
      autopay_status, autopay_failure_reason, stripe_payment_method_id,
      monthly_tuition_override_cents, sibling_discount_percent_override,
      family:families(id, display_name, primary_contact_profile_id,
        organization:organizations(id, display_name, name))
    `)
    .order("created_at", { ascending: false });
  if (error) throw error;

  const rows = profiles || [];
  if (rows.length === 0) return [];

  const profileIds = rows.map((r) => r.id);
  const { data: invs } = await supabase
    .from("invoices")
    .select("billing_profile_id, status, amount_total_cents")
    .in("billing_profile_id", profileIds);

  const totals = (invs || []).reduce((acc, inv) => {
    const bucket = acc[inv.billing_profile_id] || { open: 0, paid: 0, count: 0 };
    if (inv.status === "issued" || inv.status === "failed") {
      bucket.open += inv.amount_total_cents || 0;
    }
    if (inv.status === "paid") bucket.paid += inv.amount_total_cents || 0;
    bucket.count += 1;
    acc[inv.billing_profile_id] = bucket;
    return acc;
  }, {});

  return rows.map((r) => ({
    ...r,
    totals: totals[r.id] || { open: 0, paid: 0, count: 0 },
  }));
}

// ---------------------------------------------------------------------------
// Admin / Parent — invoices for a family
// ---------------------------------------------------------------------------

export async function getBillingProfileForFamily(familyId) {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase
    .from("billing_profiles")
    .select(`
      id, family_id, billing_email, stripe_customer_id, status, autopay,
      autopay_status, autopay_failure_reason, stripe_payment_method_id,
      autopay_enrolled_at, autopay_last_charge_at, organization_id,
      monthly_tuition_override_cents, sibling_discount_percent_override,
      family:families(id, display_name, primary_contact_profile_id,
        organization:organizations(id, display_name, name))
    `)
    .eq("family_id", familyId)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function getBillingProfileById(id) {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase
    .from("billing_profiles")
    .select(`
      id, family_id, billing_email, stripe_customer_id, status, autopay,
      monthly_tuition_override_cents,
      family:families(id, display_name)
    `)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function listInvoicesForProfile(billingProfileId, { parentView } = {}) {
  if (!isSupabaseConfigured) return [];
  let q = supabase
    .from("invoices")
    .select(`
      id, billing_profile_id, invoice_number, title, issue_date, due_date,
      amount_total_cents, currency, status, paid_at, created_at,
      invoice_emailed_at, invoice_emailed_to,
      autopay_attempt_count, autopay_next_retry_at, autopay_last_error,
      line_items:invoice_line_items(id, category, description, quantity, unit_amount_cents, sort_order)
    `)
    .eq("billing_profile_id", billingProfileId)
    .order("issue_date", { ascending: false });
  if (parentView) q = q.neq("status", "draft");
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((inv) => ({
    ...inv,
    line_items: (inv.line_items || []).sort((a, b) => a.sort_order - b.sort_order),
  }));
}

// ---------------------------------------------------------------------------
// Admin — invoice CRUD
// ---------------------------------------------------------------------------

export async function createDraftInvoice({
  organizationId, billingProfileId, title, dueDate, createdBy,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("invoices")
    .insert({
      organization_id: organizationId,
      billing_profile_id: billingProfileId,
      title,
      due_date: dueDate || null,
      created_by: createdBy || null,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function addInvoiceLineItem({
  invoiceId, category, description, quantity, unitAmountCents, sortOrder,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("invoice_line_items")
    .insert({
      invoice_id: invoiceId,
      category,
      description,
      quantity,
      unit_amount_cents: unitAmountCents,
      sort_order: sortOrder ?? 0,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function removeInvoiceLineItem(id) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { error } = await supabase.from("invoice_line_items").delete().eq("id", id);
  if (error) throw error;
}

export async function issueInvoice(invoiceId) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_set_invoice_issued", {
    p_invoice_id: invoiceId,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function voidInvoice(invoiceId) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("invoices")
    .update({ status: "void" })
    .eq("id", invoiceId)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function getOrganizationBillingDefaults(organizationId) {
  if (!isSupabaseConfigured || !organizationId) return null;
  const { data, error } = await supabase
    .from("organizations")
    .select("id, name, sibling_discount_percent")
    .eq("id", organizationId)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

export async function adminMarkInvoicePaid({ invoiceId, note }) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_admin_mark_invoice_paid", {
    p_invoice_id: invoiceId,
    p_note: note || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function updateBillingProfile(id, patch) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("billing_profiles")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------------------
// Parent — checkout via FastAPI
// ---------------------------------------------------------------------------

async function bearerToken() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return token;
}

/** Billing actions need the FastAPI backend. When REACT_APP_BACKEND_URL
 *  isn't configured, fail with a calm human message instead of letting
 *  fetch() hit the static host and surface parser gibberish. */
function requireApi() {
  if (!API_BASE) {
    throw new Error(
      "Online payments aren't available right now. Please try again later.",
    );
  }
}

/** Parses a JSON API response defensively: a non-OK status or a
 *  non-JSON body (e.g. an HTML error page from a misrouted request)
 *  becomes a readable error — never raw HTML, never a WebKit
 *  "string did not match the expected pattern" parse failure. */
async function parseJsonResponse(resp, fallback) {
  const ct = (resp.headers.get("content-type") || "").toLowerCase();
  if (!resp.ok || !ct.includes("application/json")) {
    let msg = "";
    try {
      msg = (await resp.text()).slice(0, 200);
    } catch {
      /* ignore */
    }
    if (!msg || msg.trim().startsWith("<")) {
      msg = `${fallback} (${resp.status})`;
    }
    throw new Error(msg);
  }
  return resp.json();
}

export async function startCheckoutSession(invoiceId) {
  requireApi();
  const token = await bearerToken();
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const resp = await fetch(`${API_BASE}/api/billing/checkout/session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ invoice_id: invoiceId, origin_url: origin }),
  });
  return parseJsonResponse(resp, "Checkout failed"); // { session_id, url }
}

export async function getCheckoutStatus(sessionId) {
  requireApi();
  const token = await bearerToken();
  const resp = await fetch(
    `${API_BASE}/api/billing/checkout/status/${encodeURIComponent(sessionId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return parseJsonResponse(resp, "Status check failed");
}

// ---------------------------------------------------------------------------
// Autopay (Stripe Setup-mode Checkout)
// ---------------------------------------------------------------------------

export async function startAutopaySetup(billingProfileId) {
  requireApi();
  const token = await bearerToken();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const resp = await fetch(`${API_BASE}/api/billing/autopay/setup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      billing_profile_id: billingProfileId,
      origin_url: origin,
    }),
  });
  return parseJsonResponse(resp, "Autopay setup failed"); // { session_id, url }
}

export async function getAutopayStatus(sessionId) {
  requireApi();
  const resp = await fetch(
    `${API_BASE}/api/billing/autopay/status/${sessionId}`,
  );
  return parseJsonResponse(resp, "Status check failed");
}

export async function devSyncAutopay(sessionId) {
  requireApi();
  const token = await bearerToken();
  const resp = await fetch(`${API_BASE}/api/billing/autopay/sync-dev`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "X-Session-Id": sessionId,
    },
  });
  return parseJsonResponse(resp, "Dev sync failed");
}

export async function disableAutopay(billingProfileId) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase.rpc("rpc_disable_autopay", {
    p_billing_profile_id: billingProfileId,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

// ---------------------------------------------------------------------------
// Module D · Parent payment history + invoice PDF download
// ---------------------------------------------------------------------------

/** Returns the URL to open the parent-scoped invoice PDF in a new tab.
 *  Opening via a window.open (not fetch) lets the mobile browser use
 *  its native PDF viewer and "Save to Files" integration without us
 *  having to handle the blob. */
export function parentInvoicePdfUrl(invoiceId) {
  // The endpoint is admin-JWT-guarded; the browser doesn't send our
  // Supabase session on a window.open. We therefore open a short-lived
  // signed download inside the SPA by using an <a> with the bearer in
  // a fragment-less URL. To keep this simple + mobile-friendly, we
  // fetch the PDF ourselves and hand the browser a blob: URL.
  // See listParentPaymentHistory's companion helper below.
  return `${API_BASE}/api/parent/invoices/${encodeURIComponent(invoiceId)}/pdf`;
}

/** Fetches the parent-scoped invoice PDF and opens it in a new tab. */
export async function openParentInvoicePdf(invoiceId) {
  requireApi();
  const token = await bearerToken();
  const resp = await fetch(parentInvoicePdfUrl(invoiceId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(
      resp.status === 404
        ? "The invoice PDF service isn't available right now."
        : `Could not download PDF (${resp.status})`,
    );
  }
  // Never let a misrouted HTML response masquerade as a PDF download.
  const ct = (resp.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("pdf") && !ct.includes("octet-stream")) {
    throw new Error("The invoice PDF service isn't available right now.");
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    // Popup blocker — force a download anchor as a fallback.
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice-${invoiceId.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Free the object URL after the tab has had time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Pulls the caller's family's payment_transactions in reverse chrono.
 *  Only parents with Module D RLS (migration 20270301000000) see
 *  autopay rows here — older RLS hid them behind profile_id. */
export async function listParentPaymentHistory({ limit = 200 } = {}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("payment_transactions")
    .select(`
      id, created_at, amount_cents, currency, status, payment_status,
      session_id, metadata,
      invoice:invoices(id, title, issue_date, status)
    `)
    .in("status", ["succeeded", "failed"])
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export const AUTOPAY_STATUS_META = {
  none:           { label: "Not set up",     tone: "border-neutral-200 bg-neutral-100 text-neutral-600" },
  pending_setup:  { label: "Setup in progress", tone: "border-secondary/50 bg-secondary/30 text-neutral-900" },
  active:         { label: "Autopay on",     tone: "border-primary/30 bg-primary/10 text-primary-hover" },
  setup_failed:   { label: "Setup failed",   tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#C6851E]" },
  payment_failed: { label: "Payment failed", tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#C6851E]" },
};

// ---------------------------------------------------------------------------
// Readiness chip helpers (used on admin EnrollmentDetail)
// ---------------------------------------------------------------------------

export function computeBillingReadiness({
  record, docStatus, billingProfile,
}) {
  const enrolled = record.stage === "enrolled";
  const converted = Boolean(record.linked_family_id && record.linked_child_id);
  const docsReady = docStatus
    ? docStatus.missingRequired === 0 && docStatus.rejectedCount === 0
    : false;
  const hasProfile = Boolean(billingProfile?.id);
  return {
    enrolled,
    converted,
    docsReady,
    billingProfileReady: hasProfile,
    allReady: enrolled && converted && docsReady && hasProfile,
  };
}
